import * as React from "react";

import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services";

import { RawMessageManager } from "./RawMessageManager";

// 50 ms keeps the displayed sample within roughly one frame of the timer at
// 60 fps without saturating the worker. Lower values mostly add round-trips
// the user can't perceive; higher values make seek feel laggy.
const POLL_INTERVAL_MS = 50;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Yields a tick every `ms` until `signal` aborts, pausing entirely while the
 * tab is hidden so a backgrounded panel does not accumulate stale fetches
 * (and immediately resuming on `visibilitychange`). Each tick subtracts the
 * time spent in the consumer's `await` so the cadence stays close to `ms`
 * even when work runs long.
 */
async function* interval(ms: number, signal: AbortSignal) {
  while (!signal.aborted) {
    if (document.visibilityState === "hidden") {
      await new Promise((resolve) =>
        document.addEventListener("visibilitychange", resolve, {
          once: true,
          signal,
        }),
      );
      continue;
    }

    const now = performance.now();
    yield now;
    await delay(ms - (performance.now() - now));
  }
}

/**
 * Drives `manager.loadMessages(signal)` once per tick of `interval(intervalMs)`
 * until `signal` aborts. Exported for unit testing only; production code
 * mounts the `useMessages` hook below, which owns the signal's lifetime.
 *
 * Failure-mode posture:
 *
 * 1. `AbortError` from `loadMessages` ends the loop silently. An abort means
 *    the panel is tearing down or the manager is being swapped, so the next
 *    poll cycle (if any) will start fresh against the new manager.
 * 2. Any other error is reported to `ErrorMonitoringService` and the loop
 *    continues. A single failed fetch should not stop the panel from catching
 *    up on subsequent ticks.
 */
export async function pollForMessages(
  manager: RawMessageManager,
  signal: AbortSignal,
  intervalMs: number = POLL_INTERVAL_MS,
): Promise<void> {
  for await (const _ of interval(intervalMs, signal)) {
    if (signal.aborted) {
      break;
    }
    try {
      await manager.loadMessages(signal);
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }
      ErrorMonitoringService.captureError(err);
    }
  }
}

type Options = {
  /** Whether to poll for messages. Defaults to `true`. */
  enabled?: boolean;
};

/**
 * Polls `manager.loadMessages` on behalf of a mounted `RawMessagePanel` so the
 * panel keeps catching up to the timer's current sample as the user plays,
 * scrubs, or seeks. No-ops while `enabled` is false or `manager` is `null`.
 *
 * 1. Each mount (or change to `enabled` / `manager`) creates a fresh
 *    `AbortController`; cleanup aborts it, which both stops the polling loop
 *    and cancels any worker request `loadMessages` has in flight.
 * 2. The loop swallows `AbortError` and routes every other thrown error to
 *    `ErrorMonitoringService` (see `pollForMessages`), so a transient worker
 *    failure neither crashes the panel nor halts subsequent polls.
 */
export function useMessages(
  manager: RawMessageManager | null,
  options?: Options,
): void {
  const { enabled = true } = options ?? {};

  React.useEffect(() => {
    if (!enabled || manager === null) {
      return;
    }
    const abortController = new AbortController();
    void pollForMessages(manager, abortController.signal);

    return () => {
      abortController.abort();
    };
  }, [enabled, manager]);
}
